import { cloneDeep } from 'lodash';
import { formatTimestampToMinute } from "utils/format";

const normalizeDeviceCounts = (devices) => ({
  accessPoints: devices?.ap ?? devices?.accessPoints ?? 0,
  switches: devices?.switch ?? devices?.switches ?? 0,
  routers: devices?.router ?? devices?.routers ?? 0,
});

const normalizeClientCount = (clients) => {
  if (typeof clients === "number") return clients;
  return (clients?.wired ?? 0) + (clients?.wireless ?? 0);
};

const normalizeAlerts = (alerts) => ({
  critical: alerts?.critical ?? 0,
  normal: alerts?.normal ?? 0,
  low: alerts?.low ?? 0,
});

const normalizeDeviceStatus = (devices, fallbackOnline = 0) => ({
  online: devices?.online ?? fallbackOnline,
  offline: devices?.offline ?? 0,
  onboarding: devices?.onboarding ?? 0,
});

const normalizeSurveillanceDeviceStatus = (devices) => ({
  online: devices?.online ?? 0,
  offline: devices?.offline ?? 0,
  onboarding: devices?.onboarding ?? 0,
});

const mergeRealmData= (entList, aloneOrgList, { system = "network" } = {}) => {
  let resultAry = []
  
  let enterprisesList = []
  if(entList.data && entList.data.length > 0){
    enterprisesList = entList.data
  }
  let standAloneList = []
  if(aloneOrgList.data && aloneOrgList.data.length > 0){
    standAloneList = aloneOrgList.data
  }

  for(let childEnt of enterprisesList){
    let child = {
      id: childEnt.entId,
      name: childEnt.entName,
      hasManageOrg: true,
      roleId: childEnt.entRoleId,
      roleName: childEnt.entRoleName,
      createdAt: childEnt.createdAt,
      createdTime: formatTimestampToMinute(childEnt.createdAt),
      type: "MSP",
      organizationCount: childEnt.orgNum ?? childEnt.organizationCount ?? 0,
      siteCount: childEnt.siteNum ?? childEnt.siteCount ?? 0,
      ...(system === "surveillance"
        ? {
            cameras: normalizeSurveillanceDeviceStatus(childEnt.cameraNum),
            nvrs: normalizeSurveillanceDeviceStatus(childEnt.nvrNum),
          }
        : {
            adoptedDevices: normalizeDeviceCounts(
              childEnt.adoptedDeviceNum ?? childEnt.adoptedDevices,
            ),
            inventoryDevices: normalizeDeviceCounts(
              childEnt.inventoryDeviceNum ?? childEnt.inventoryDevices,
            ),
            clients: normalizeClientCount(childEnt.clientNum ?? childEnt.clients),
            alerts: normalizeAlerts(childEnt.alertNum ?? childEnt.alerts),
          }),
      enableAction: (childEnt.entRoleId === 1 || childEnt.entRoleId === 2)? true:false
    }
    resultAry.push(child)
  }
  for(let childAloneOrg of standAloneList){
    let child = {
      id: childAloneOrg.orgId,
      name: childAloneOrg.orgName,
      hasManageOrg: false,
      roleId: childAloneOrg.orgRoleId,
      roleName: childAloneOrg.orgRoleName,
      createdAt: childAloneOrg.createdAt,
      createdTime: formatTimestampToMinute(childAloneOrg.createdAt),
      type: "Standalone",
      organizationCount: null,
      siteCount: childAloneOrg.siteNum ?? childAloneOrg.siteCount ?? 0,
      ...(system === "surveillance"
        ? {
            cameras: normalizeSurveillanceDeviceStatus(childAloneOrg.cameraNum),
            nvrs: normalizeSurveillanceDeviceStatus(childAloneOrg.nvrNum),
          }
        : {
            adoptedDevices: normalizeDeviceCounts(
              childAloneOrg.adoptedDeviceNum ?? childAloneOrg.adoptedDevices,
            ),
            inventoryDevices: normalizeDeviceCounts(
              childAloneOrg.inventoryDeviceNum ?? childAloneOrg.inventoryDevices,
            ),
            clients: normalizeClientCount(
              childAloneOrg.clientNum ?? childAloneOrg.clients,
            ),
            alerts: normalizeAlerts(childAloneOrg.alertNum ?? childAloneOrg.alerts),
          }),
      enableAction: (childAloneOrg.orgRoleId === 5 || childAloneOrg.orgRoleId === 6)? true:false
    }
    resultAry.push(child)
  }
  
  return resultAry;
}

const mergeOrgData= (orgList, { system = "network", licenseSummary } = {}) => {
  let resultAry = []
  const remainingDaysByOrgId = new Map(
    (Array.isArray(licenseSummary?.data) ? licenseSummary.data : []).map((org) => {
      return [org.orgId, org.remainingDays || 0];
    }),
  );
  
  let manageOrgList = []
  if(orgList.data && orgList.data.orgs.length > 0){
    manageOrgList = orgList.data.orgs
  }

  for(let childEnt of manageOrgList){
    const adoptedDevices = normalizeDeviceCounts(
      childEnt.adoptedDeviceNum ?? childEnt.adoptedDevices,
    );
    let child = {
      id: childEnt.orgId,
      name: childEnt.orgName,
      roleId: childEnt.orgRoleId,
      roleName: childEnt.orgRoleName,
      siteCount: childEnt.siteNum ?? childEnt.siteCount ?? 0,
      daysLeft: remainingDaysByOrgId.get(childEnt.orgId) ?? 0,
      ...(system === "surveillance"
        ? {
            cameras: normalizeSurveillanceDeviceStatus(childEnt.cameraNum),
            nvrs: normalizeSurveillanceDeviceStatus(childEnt.nvrNum),
          }
        : {
            adoptedDevices,
            accessPoints: normalizeDeviceStatus(null, adoptedDevices.accessPoints),
            switches: normalizeDeviceStatus(null, adoptedDevices.switches),
            inventoryDevices: normalizeDeviceCounts(
              childEnt.inventoryDeviceNum ?? childEnt.inventoryDevices,
            ),
            clients: normalizeClientCount(childEnt.clientNum ?? childEnt.clients),
            alerts: normalizeAlerts(childEnt.alertNum ?? childEnt.alerts),
          }),
      createdAt: childEnt.createdAt,
      createdTime: formatTimestampToMinute(childEnt.createdAt),
    }
    resultAry.push(child)
  }
  
  return resultAry;
}

const mergeSiteData= (siteList) => {
  let resultAry = []
  
  let orgSiteList = []
  if(siteList.data && siteList.data.sites.length > 0){
    orgSiteList = siteList.data.sites
  }

  for(let childEnt of orgSiteList){
    let child = {
      id: childEnt.siteId,
      name: childEnt.siteName,
      roleId: childEnt.siteRoleId,
      roleName: childEnt.siteRoleName,
      country: childEnt.country ?? "",
      accessPoints: normalizeDeviceStatus(
        childEnt.apNum ?? childEnt.accessPoints,
      ),
      switches: normalizeDeviceStatus(
        childEnt.switchNum ?? childEnt.switches,
      ),
      routers: normalizeDeviceStatus(
        childEnt.routerNum ?? childEnt.routers,
      ),
      cameras: normalizeSurveillanceDeviceStatus(
        childEnt.cameraNum ?? childEnt.ipcamNum ?? childEnt.cameras,
      ),
      nvrs: normalizeSurveillanceDeviceStatus(
        childEnt.nvrNum ?? childEnt.nvrs,
      ),
      clients: {
        wireless: childEnt.clientNum?.wireless ?? childEnt.clients?.wireless ?? 0,
        wired: childEnt.clientNum?.wired ?? childEnt.clients?.wired ?? 0,
      },
      alerts: normalizeAlerts(childEnt.alertNum ?? childEnt.alerts),
      createdAt: childEnt.createdAt,
      createdTime: formatTimestampToMinute(childEnt.createdAt),
    }
    resultAry.push(child)
  }
  
  return resultAry;
}

export default {
  mergeRealmData,
  mergeOrgData,
  mergeSiteData
};
